import { isNil } from 'lodash-es';
import { storeToRefs } from 'pinia';
import {
  GLOBAL_VARIABLE_TAG_KEY,
  TREE_GLOBAL_VARIABLE_TAG_KEY,
} from '@/common/define/widget.define';
import { FIXED_DATA_ID_BY_CHART_TYPE } from '@/dashboard/features/widgets/common/constants/define';
import { WidgetTarget } from '@/dashboard/shared/types/widgets.types';
import { TagRequest, TagRequestTagType } from '@/api-client/data/model';
import { useStandardVariablesStore } from '@/dashboard/stores/global-variables/standard-variables';
import { useTreeVariablesStore } from '@/dashboard/stores/global-variables/tree-variables';
import { useDashboardTargetsStore } from '@/dashboard/stores/dashboard-targets';
import { useDashboardLegendStore } from '@/dashboard/stores/dashboard-legend';
import type { SelectedLegends } from '@/dashboard/features/dashboardLegend/domain/dashboardLegend.types';
import { useDashboardViewStore } from '@/dashboard/stores/dashboard-view';
import { TREE_VARIABLE_TAG_TYPE } from '@/dashboard/features/globalVariables/domain/treeVariable.constants';

type FilteredTagsResult = {
  tags?: TagRequest[];
  userTags?: TagRequest[];
  targetIds?: string[];
};

const addTagToMap = (
  map: Map<string, TagRequest>,
  mapKey: string,
  tagsInfo: {
    globalVariableId?: string;
    tagKey: string;
    tagType: TagRequestTagType;
    tagValue: string;
    matchedTag?: boolean;
  },
): void => {
  const { globalVariableId, tagKey, tagType, tagValue, matchedTag } = tagsInfo;
  const existing = map.get(mapKey);
  if (existing) {
    existing.tagValue?.push(tagValue);
    return;
  }
  map.set(mapKey, {
    ...(globalVariableId && { globalVariableId }),
    tagKey: tagKey ?? '',
    tagType,
    tagValue: [tagValue],
    ...(matchedTag && { matchedTag }),
  });
};

const extractGlobalFilterIdList = (tags: TagRequest[]): string[] =>
  tags.reduce<string[]>((acc, { tagType, tagValue }) => {
    if (tagType === 'globalVariable') {
      tagValue?.forEach((v) => {
        if (v != null) acc.push(v);
      });
    }
    return acc;
  }, []);

const extractTreeFilterIdList = (tags: TagRequest[]): string[] =>
  tags.reduce<string[]>((acc, { tagType, tagValue }) => {
    if (tagType === 'treeGlobalVariable') {
      tagValue?.forEach((v) => {
        if (v != null) acc.push(v);
      });
    }
    return acc;
  }, []);

export const useRequestTags = () => {
  const { selectedStandardVariables } = storeToRefs(useStandardVariablesStore());
  const treeVariablesStore = useTreeVariablesStore();
  const { selectedTreeVariables } = storeToRefs(treeVariablesStore);
  const { selectedLegends } = storeToRefs(useDashboardLegendStore());
  const { isVariableFilterDisabled } = storeToRefs(useDashboardViewStore());
  const { getBaselineTargetIdSet } = useDashboardTargetsStore();

  const getRequestTags = (targets: WidgetTarget[]): TagRequest[] => {
    const useGlobalVariableIds = selectedStandardVariables.value
      .filter(({ use }) => use !== false)
      .map(({ globalFilterId }) => `${globalFilterId}`);

    const useTreeVariableIds = selectedTreeVariables.value
      .filter(({ use }) => use !== false)
      .map(({ treeVariableId }) => `${treeVariableId}`);

    const filteredTargets =
      selectedStandardVariables.value.length > 0 || selectedTreeVariables.value.length > 0
        ? targets.filter(({ tagType, tagValue }) => {
            if (tagType === 'globalVariable') {
              return useGlobalVariableIds.includes(tagValue);
            }
            if (tagType === 'treeGlobalVariable') {
              return useTreeVariableIds.includes(tagValue);
            }
            return true;
          })
        : targets;

    const tagsMap = new Map<string, TagRequest>();
    filteredTargets.forEach(({ tagKey, tagType, tagValue }) => {
      addTagToMap(tagsMap, `${tagKey}${tagType}`, {
        tagKey,
        tagType: tagType as TagRequestTagType,
        tagValue,
      });
    });

    return Array.from(tagsMap.values());
  };

  const getRequestUserTags = (
    globalFilterIdList?: string[],
    treeFilterIdList?: string[],
  ): TagRequest[] => {
    const useGlobalVariables = selectedStandardVariables.value.filter(
      ({ use, globalFilterId }) =>
        use !== false &&
        (isNil(globalFilterIdList) ||
          (globalFilterIdList.length > 0 && globalFilterIdList.includes(`${globalFilterId}`))),
    );

    const userTagsMap = new Map<string, TagRequest>();

    useGlobalVariables.forEach(({ globalFilterId, selectedList }) => {
      if (selectedList?.length) {
        selectedList.forEach(({ groupTagId, tagId, tagType }) => {
          addTagToMap(userTagsMap, `${groupTagId}${tagType}`, {
            tagKey: groupTagId ?? '',
            tagType: tagType as TagRequestTagType,
            tagValue: tagId,
          });
        });
        return;
      }

      addTagToMap(userTagsMap, `${globalFilterId}`, {
        tagKey: GLOBAL_VARIABLE_TAG_KEY,
        tagType: 'globalVariable',
        tagValue: `${globalFilterId}`,
      });
    });

    const useTreeVariables = selectedTreeVariables.value.filter(
      ({ use, treeVariableId }) =>
        use !== false &&
        (isNil(treeFilterIdList) ||
          (treeFilterIdList.length > 0 && treeFilterIdList.includes(`${treeVariableId}`))),
    );

    useTreeVariables.forEach(({ treeVariableId, operationType, selectedList }) => {
      if (selectedList?.length) {
        const resolvedTagType = TREE_VARIABLE_TAG_TYPE.MANUAL[operationType] as TagRequestTagType;
        const treeVar = treeVariablesStore.getTreeVariableById(treeVariableId);
        const lastIndex = (treeVar?.depths.length ?? 1) - 1;
        const isLeafSelection = (treeVar?.depths[lastIndex]?.selectedList.length ?? 0) > 0;

        selectedList.forEach(({ tagKey, tagValueId }) => {
          addTagToMap(userTagsMap, `${treeVariableId}${tagKey}${resolvedTagType}`, {
            globalVariableId: `${treeVariableId}`,
            tagKey,
            tagType: resolvedTagType,
            tagValue: tagValueId,
            ...(isLeafSelection && { matchedTag: true }),
          });
        });
      } else {
        addTagToMap(userTagsMap, `tree_${treeVariableId}`, {
          globalVariableId: `${treeVariableId}`,
          tagKey: TREE_GLOBAL_VARIABLE_TAG_KEY,
          tagType: 'treeGlobalVariable',
          tagValue: `${treeVariableId}`,
        });
      }
    });

    return Array.from(userTagsMap.values());
  };

  const buildTagsAndUserTags = (
    targets: WidgetTarget[],
  ): { tags: TagRequest[]; userTags: TagRequest[] } => {
    if (isVariableFilterDisabled.value) {
      const nonGlobalTags = targets
        .filter(({ tagType }) => tagType !== 'globalVariable' && tagType !== 'treeGlobalVariable')
        .reduce((map, { tagKey, tagType, tagValue }) => {
          addTagToMap(map, `${tagKey}${tagType}`, {
            tagKey,
            tagType: tagType as TagRequestTagType,
            tagValue,
          });
          return map;
        }, new Map<string, TagRequest>());
      return { tags: Array.from(nonGlobalTags.values()), userTags: [] };
    }

    const tags = getRequestTags(targets);
    const userTags = getRequestUserTags(
      extractGlobalFilterIdList(tags),
      extractTreeFilterIdList(tags),
    );
    return { tags, userTags };
  };

  const getFilteredTags = ({
    targets,
    legends,
    widgetUUID,
    chartDataId,
    dataId,
  }: {
    targets: WidgetTarget[];
    legends?: SelectedLegends[] | null;
    widgetUUID: string;
    chartDataId: string;
    dataId?: string;
  }): FilteredTagsResult => {
    const { tags, userTags } = buildTagsAndUserTags(targets);
    const resolvedLegends = legends !== undefined ? legends : selectedLegends.value;

    if (resolvedLegends === null) {
      return { tags: [], userTags: [] };
    }

    if (dataId === FIXED_DATA_ID_BY_CHART_TYPE.TOPOLOGY) {
      return { tags, userTags };
    }

    if (!resolvedLegends.length) {
      return { tags, userTags };
    }

    const baseline = getBaselineTargetIdSet(widgetUUID, chartDataId);
    if (baseline.size === 0) {
      return { targetIds: [] };
    }

    const targetIds: string[] = [];
    const seen = new Set<string>();
    resolvedLegends.forEach(({ targetId }) => {
      if (targetId == null) return;
      const key = `${targetId}`;
      if (!baseline.has(key) || seen.has(key)) return;
      seen.add(key);
      targetIds.push(key);
    });

    return { targetIds };
  };

  /**
   * baseline 좁힘이 적용되지 않는 fallback caller 전용 — ctx (widgetUUID / chartDataId) 가 없는
   * 호출처용. tags / userTags 는 그대로 반환되고 좁힘은 하지 않는다.
   *
   * `legends` 인자는 **`=== null` 신호 (전체 해제, 응답 0건 의도) 전용**. null 일 때만
   * `{ tags: [], userTags: [] }` 빈 결과를 돌려준다. `[]` / non-empty / undefined 는 모두 동일하게
   * pass-through 결과가 나오므로 호출부에서 "legends 로 필터된다" 는 인상을 갖지 않도록 주의.
   * scatter summary popup 처럼 store 의 selectedLegends 와 다른 출처의 legends 가 있을 때 명시
   * 전달하는 용도. 그 외 호출처는 `legends` 인자를 생략하고 store 의 selectedLegends 가 default
   * 로 적용되게 한다.
   */
  const getFilteredTagsPassThrough = ({
    targets,
    legends,
  }: {
    targets: WidgetTarget[];
    legends?: SelectedLegends[] | null;
  }): FilteredTagsResult => {
    const { tags, userTags } = buildTagsAndUserTags(targets);
    const resolvedLegends = legends !== undefined ? legends : selectedLegends.value;

    if (resolvedLegends === null) {
      return { tags: [], userTags: [] };
    }
    return { tags, userTags };
  };

  return {
    getRequestTags,
    getRequestUserTags,
    getFilteredTags,
    getFilteredTagsPassThrough,
  };
};
